import React, { useState, useEffect, useCallback } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/20/solid'
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import Modal from '../components/Modal';
import ShortDateFormat from '../components/ShortDateFormat';
import PhoneNumber from '../components/PhoneNumber';
import LoadingSpinner from '../components/LoadingSpinner';
import Accordion from '../components/Accordion';
import { FlagIcon, TrashIcon } from '@heroicons/react/24/outline';
import SelectParts from '../components/SelectParts';
import { set } from 'date-fns';


const TechWipDetails = () => {
  const navigate = useNavigate();
  const [job, setJob] = useState([]);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isNoCharge, setIsNoCharge] = useState(false);
  const [techNotes, setTechNotes] = useState('');
  const [openIndex, setOpenIndex] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [serviceEmail, setServiceEmail] = useState({
    customerEmail: '',
    customerPrint: '',
    noEmail: false
  });

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const [userId, setUserId] = useState(null); // State to hold user ID

  useEffect(() => {
    setLoading(true);
    fetchJob(); //service call
   }, [apiUrl, navigate, id]);

  useEffect(() => {
    if (job?.TYPE) {
      setIsNoCharge(job.TYPE === 'NO CHARGE');
    }
  }, [job]);

  const openInMaps = () => {
    const fullAddress = [
      job?.job_site
    ].filter(Boolean).join(' ');
    if (!fullAddress.trim()) {
      toast?.error?.("No job site address available", { position: 'top-center' });
      return;
    }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`, '_blank');
  };    
 
  const fetchJob = async () => { 
    try {
      const response = await axios.get(`${apiUrl}/wip_crud.php?getWipTechDetail=${id}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        const jobData = response.data[1] ?? [];
        setJob(jobData);
        setUserId(response.data[2] || null); // assuming data[2] contains user info
        const addTechNotes = jobData.tech_notes || ''; // Ensure TECHNOTES is set to an empty string if it's not available
        if (addTechNotes.length > 0) {
          setIsReady(true);
        }
        setTechNotes(addTechNotes);
        
      } else {
        toast.error(response.data.error, { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: "top-center" });
      }
    } finally {
      setLoading(false);
    }
  };
  const handleTechNotesChange = (e) => {
    setTechNotes(e.target.value); // Correctly update the state with the input value
  };

  const handleSubmitTechNotes = async () => {
    setLoading(true);

    const bodyData = {
      status: "addTechNotes", // Assuming this is the correct status for your backend
      tech_notes: techNotes,  // Send the updated tech notes
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        setIsReady(true);
        setJob((prevState) => ({ ...prevState, tech_notes: techNotes, }));
        toast.success('Tech notes updated!', { position: 'top-center' });
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComplete = async () => {
    setLoading(true);

    const bodyData = {
      status: "wipCompleted", // Assuming this is the correct status for your backend
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        toast.success('WIP completed successfully!', { position: 'top-center' });
        navigate('/t/twiplist'); // Redirect to the WIP list after successful completion
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async () => {
    setLoading(true);

    const bodyData = {
      status: "wipFlagged", // Assuming this is the correct status for your backend
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        toast.success('WIP flagged successfully!', { position: 'top-center' });
        navigate('/t/twiplist'); // Redirect to the WIP list after successful completion
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

return (
  <div className="min-h-screen bg-gray-50 pb-24">
    {/* Header */}
    <div className="px-4 pt-4">
      <h1 className="text-xl font-semibold text-gray-900">WIP Details</h1>
      <p className="text-xs text-gray-500">Tech view</p>
    </div>

    {/* Loading state */}
    {loading && (
      <div className="px-4 py-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-2/3 rounded bg-gray-200" />
          <div className="h-20 w-full rounded-xl bg-gray-200" />
          <div className="h-32 w-full rounded-xl bg-gray-200" />
        </div>
      </div>
    )}

    {!loading && job && (
      <div className="px-4 py-4 space-y-4">

        {/* Summary Card */}
        <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">
          {/* Chips row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Type chip */}
            {job.type && (
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                {job.type}
              </span>
            )}
            {/* Status chip */}
            {job.status && (
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset
                ${String(job.status).toLowerCase().includes('complete')
                  ? 'bg-green-50 text-green-700 ring-green-600/20 border-transparent'
                  : String(job.status).toLowerCase().includes('tentative')
                  ? 'bg-red-50 text-red-700 ring-red-600/20 border-transparent'
                  : 'bg-gray-50 text-gray-700 ring-gray-300/50 border-gray-200'}`}>
                {job.status}
              </span>
            )}
            {/* Job # */}
            {job.job_number && (
              <span className="ml-auto text-xs font-semibold text-gray-500">
                Job #{job.job_number}
              </span>
            )}
          </div>

          {/* additional tech */}
          {job.additional_tech && (
            <div className="mt-3 rounded-lg bg-gray-50 p-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">Additional Tech</div>
              <div className="mt-0.5 font-medium text-blue-700">{job.additional_tech}</div>
            </div>
          )}

          {/* Date / Time */}
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg bg-gray-50 p-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Dates</div>
              <div className="mt-0.5 font-medium text-gray-900">
                {job.schedule_from ? <ShortDateFormat date={job.schedule_from}/> : '—'}
                <span className="mx-1 text-gray-400">→</span>
                {job.schedule_to ? <ShortDateFormat date={job.schedule_to}/> : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Time</div>
              <div className="mt-0.5 font-medium text-gray-900">
                {job.start_time || '—'}{job.end_time ? ` – ${job.end_time}` : ''}
              </div>
            </div>
          </div>

          {/* Location / Description */}
          <div className="mt-2 rounded-lg bg-gray-50 p-2">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Site</div>
            <div className="mt-0.5 text-sm font-semibold text-gray-900">{job.job_site || '—'}</div>
            {job.job_description && (
              <div className="mt-1 text-sm text-green-700">{job.job_description}</div>
            )}
            {job.user_comment && (
              <div className="mt-1 text-xs text-gray-500">
                <span className="font-medium text-gray-600">Coordinator:</span>{' '}
                <p className='whitespace-pre-wrap'>{job.user_comment}</p>
              </div>
            )}
          </div>
        </div>

        {job.links && job.links.length > 0 && (
          <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Related Links</div>
            <dd className="mt-0.5 text-gray-900">
              <ul className="list-disc pl-5">
                {job.links.map((l) => (
                  <li key={l.id} className="flex items-center gap-3">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {l.label || 'Untitled'}
                    </span>
                    <span className="text-gray-300">→</span>
                    <a
                      href={
                        l.link && /^https?:\/\//i.test(l.link) ? l.link : `https://${l.link}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="truncate text-sm text-indigo-600 hover:underline"
                      title={l.link}
                    >
                      {l.link}
                    </a>
                  </li>
                ))}                      
              </ul>
            </dd>            
          </div>
        )}

                 {/* Action buttons */}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={openInMaps}
              className="flex-1 rounded-xl border border-gray-300 bg-gray-600 px-4 py-2.5 
                        text-sm font-semibold text-white shadow-sm hover:bg-gray-700 
                        focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Open in Maps
            </button>
          </div>

        {job.tech_id !== userId ? (<p className="text-sm text-red-700">This section is for Lead Techs only. Please reach out to your lead for more information or assistance..</p>) : (
          <>
          {/* Tech Notes Card */}
          <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Tech’s Notes</h2>
              {isReady && (
                <span className="text-[11px] rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-600/20">
                  Saved
                </span>
              )}
            </div>

            <textarea
              value={techNotes || ''}
              onChange={handleTechNotesChange}
              rows={5}
              className="mt-3 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Add your notes for this job…"
            />

            {/* Inline actions (secondary) */}
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={handleSubmitTechNotes}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Add Notes
              </button>

              {isReady && (
                <>
                  <button
                    onClick={handleFlag}
                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Flag this job"
                  >
                    <FlagIcon className="h-4 w-4" /> Flag
                  </button>

                  <button
                    onClick={handleSubmitComplete}
                    className="inline-flex items-center rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    Complete
                  </button>
                </>
              )}
            </div>
          </div>          
          </>
        )}
      </div>
    )}

    {/* Sticky footer action bar (big tap targets) */}
    {!loading && (
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-md gap-2">
          <button
            onClick={() => navigate('/t/twiplist')}
            className="w-1/2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            WIP Queue
          </button>
          <button
            onClick={()=> navigate('/t/tech_schedule')}
            className="w-1/2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Tech Schedules
          </button>
        </div>
      </div>
    )}
  </div>
);

};

export default TechWipDetails;
